<template>
    <div class="login-body">
        <div class="login-wrapper">
            <div class="login-panel">
                <router-link to="/" class="logo" id="pha-logo">
                    <img src="layout/images/PHA logo-white.png" alt="pha-logo" />
                </router-link>
                <ScaleLoader v-if="state.loader" />
                <div class="login-form">
                    <h3>Login To Your Account</h3>
                    <p>Use provided credentials to log in to your account</p>

                    <!-- SSO email check (default) -->
                    <template v-if="!showLoginFields">
                        <InputText maxlength="255" type="email" v-model="ssoEmail" placeholder="Email" required="true" />
                        <Button label="LOGIN" type="button" @click.prevent="checkSSO"></Button>
                    </template>

                    <!-- Password login when SSO unavailable -->
                    <template v-else>
                        <InputText maxlength="255" type="email" v-model="state.user.email" placeholder="Email" required="true" />
                        <InputText maxlength="255" type="password" v-model="state.user.password" placeholder="Password" required="true" />
                        <InputText maxlength="255" type="text" v-if="state.otpEnabled" v-model="state.user.mail_otp" placeholder="Enter your OTP here" />
                        <Button label="LOGIN" type="button" @click.prevent="loginUser" :disabled="state.otpModeGodUser"></Button>
                        <InputText v-if="state.otpModeGodUser" maxlength="6" type="text" v-model="state.user.god_user_otp" placeholder="Enter OTP" class="mt-3" />
                        <Button v-if="state.otpModeGodUser" label="Verify OTP" type="button" @click.prevent="verifyOtp" />

                        <p class="login-links mt-2">
                            <a href="" @click.prevent="useSSO">Use Single Sign-On</a>
                            <router-link to="/forgotPassword">Forgotten Password</router-link>
                        </p>
                    </template>

                    <Toast class="custom-toast" />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import useValidate from '@vuelidate/core';
import { required, email, helpers } from '@vuelidate/validators';
import { reactive, computed } from 'vue';
import { mapGetters, mapState } from 'vuex';
export default {
    name: 'Login',
    setup() {
        const state = reactive({
            user: {
                email: '',
                password: '',
                mail_otp: '',
                platform: 'pha',
                god_user_otp: null,
            },
            otpEnabled: false,
            otpModeGodUser: false,
            loader: false,
        });

        const rules = computed(() => {
            return {
                user: {
                    email: {
                        required: helpers.withMessage('Email field is empty', required),
                        email: helpers.withMessage('Email is invalid', email),
                    },
                    password: {
                        required: helpers.withMessage('Password field is empty', required),
                    },
                },
            };
        });

        const v$ = useValidate(rules, state);

        return {
            state,
            v$,
        };
    },
    computed: {
        ...mapGetters('auth', ['isLoggedIn']),
        ...mapState('sso', ['showLoginFields']),
        ssoEmail: {
            get() {
                return this.$store.state.sso.email;
            },
            set(value) {
                this.$store.commit('sso/setEmail', value);
            },
        },
    },
    methods: {
        async checkSSO() {
            if (!this.ssoEmail) {
                this.showError('Please enter your email');
                return;
            }

            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(this.ssoEmail)) {
                this.$toast.add({
                    severity: 'warn',
                    summary: 'Invalid Email',
                    detail: 'Please enter a valid email address',
                    life: 5000,
                });
                return;
            }

            this.state.loader = true;
            try {
                const result = await this.$store.dispatch('sso/checkSSO');
                if (result?.ok && result?.sso === false) {
                    // Pre-fill password form email when falling back
                    this.state.user.email = this.ssoEmail;
                } else if (result?.ok === false && result?.reason === 'no_url') {
                    this.showError('SSO failed. No redirect URL.');
                } else if (result?.ok === false && result?.reason === 'request_failed') {
                    this.showError('Something went wrong during SSO.');
                }
            } finally {
                this.state.loader = false;
            }
        },

        useSSO() {
            this.$store.commit('sso/setShowLoginFields', false);
        },

        async loginUser() {
            if (!this.validateFormData()) return;

            this.state.loader = true;

            try {
                const data = await this.$store.dispatch('auth/login', this.state.user);

                if (data?.data?.god_user_otp_required) {
                    this.state.otpModeGodUser = true;
                    this.$toast.add({
                        severity: 'info',
                        summary: 'OTP Sent',
                        detail: 'Enter OTP to continue',
                        life: 5000,
                    });
                    return;
                }

                if (data.status === 'Unauthorized') {
                    this.showError('Email or Password incorrect');
                    return;
                }

                if (data.status === 202) {
                    this.state.otpEnabled = true;
                    this.$toast.add({
                        severity: 'success',
                        summary: 'OTP Sent',
                        detail: 'Enter your OTP and login',
                        life: 5000,
                    });
                    return;
                }

                if (this.isLoggedIn) {
                    this.$router.push('/');
                }
            } catch (err) {
                this.showError('Something went wrong, please try again.');
            } finally {
                this.state.loader = false;
            }
        },
        showError(message) {
            this.$toast.add({
                severity: 'error',
                summary: 'Error',
                detail: message,
                life: 5000,
            });
        },

        async verifyOtp() {
            this.state.loader = true;

            try {
                const data = await this.$store.dispatch('auth/verifyOtp', this.state.user);

                if (data.status === 401) {
                    this.showError('Invalid OTP');
                    return;
                }

                if (data.statusCode === 200) {
                    this.$router.push('/');
                    return;
                }

                // handle unexpected response
                this.showError('Unexpected response, please try again.');
            } catch (err) {
                this.showError('Something went wrong, please try again.');
            } finally {
                this.state.loader = false;
            }
        },

        validateFormData() {
            // validates the fields
            this.v$.$validate();
            if (!this.v$.$error) {
                return true;
            }
            if (this.v$.user.email.$error) {
                this.toastMessage('error', this.v$.user.email.$errors[0].$message);
            }
            if (this.v$.user.password.$error) {
                this.toastMessage('error', this.v$.user.password.$errors[0].$message);
            }
        },

        toastMessage(type, message) {
            this.$toast.add({ severity: type, summary: 'Input Errors', detail: message, life: 3000 });
        },
    },
};
</script>

<style scoped>
.login-body .login-wrapper .login-panel {
    justify-content: center;
}
.login-body .login-wrapper .login-panel .login-form {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;

    h3,
    .field-checkbox label {
        color: #c3ccdd;
    }
}

.login-form :deep(.p-inputtext),
.login-form :deep(.p-button),
.login-form .login-links {
    width: 100%;
    max-width: 310px;
    box-sizing: border-box;
}

.login-form :deep(.p-inputtext) {
    margin-bottom: 20px;
}

.login-form .login-links {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-left: auto;
    margin-right: auto;
    padding: 0;
}
</style>
